import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Megaphone, X } from 'lucide-react';
import { pickStaffBanner, usePublicStaffBanners, type StaffPageUpdate } from '../../hooks/useSitePageUpdates';

function sessionDismissKey(u: StaffPageUpdate, scope: 'global' | 'page') {
  return `d88_staff_banner_dismiss_${scope}_${u.updatedAt}`;
}

/**
 * Staff-controlled banner under nav. Global slot only on /district; other routes use prefix match.
 */
export const SiteStaffBanner: React.FC = () => {
  const { pathname } = useLocation();
  const byPath = usePublicStaffBanners();

  const update = useMemo(() => pickStaffBanner(pathname, byPath), [pathname, byPath]);

  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    setDismissed(false);
  }, [pathname, update?.updatedAt]);

  if (!update) return null;

  const key = sessionDismissKey(update, pathname === '/district' || pathname === '/' ? 'global' : 'page');
  try {
    if (typeof window !== 'undefined' && sessionStorage.getItem(key) === '1') return null;
  } catch {
    /* ignore */
  }
  if (dismissed) return null;

  const dismiss = () => {
    try {
      sessionStorage.setItem(key, '1');
    } catch {
      /* ignore */
    }
    setDismissed(true);
  };

  return (
    <div className="bg-gradient-to-r from-amber-950/50 via-red-950/30 to-gray-900/80 border-b border-amber-800/30 px-4 py-2.5">
      <div className="max-w-7xl mx-auto flex items-start gap-3">
        <Megaphone size={16} className="text-amber-400 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          {update.headline?.trim() && <p className="text-sm font-semibold text-white">{update.headline}</p>}
          {update.body?.trim() && <p className="text-xs text-gray-400 mt-0.5">{update.body}</p>}
          {update.ctaLabel?.trim() && update.ctaHref?.trim() && (
            /^https?:\/\//i.test(update.ctaHref) ? (
              <a
                href={update.ctaHref}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-2 text-xs font-semibold text-amber-400 hover:text-amber-300"
              >
                {update.ctaLabel} →
              </a>
            ) : (
              <Link
                to={update.ctaHref.startsWith('/') ? update.ctaHref : `/${update.ctaHref}`}
                className="inline-block mt-2 text-xs font-semibold text-amber-400 hover:text-amber-300"
              >
                {update.ctaLabel} →
              </Link>
            )
          )}
        </div>
        <button type="button" onClick={dismiss} className="p-1 text-gray-500 hover:text-white shrink-0" aria-label="Dismiss banner">
          <X size={16} />
        </button>
      </div>
    </div>
  );
};
